"use client";

import * as React from "react";
import { X } from "lucide-react";
import { Sidebar } from "./sidebar";
import { Topbar } from "./topbar";
import { PageTitleProvider } from "./page-title";
import { cn } from "@/lib/cn";

/**
 * App shell — Volume 02 §6.4.
 * Desktop: sidebar 256px (thu gọn 72px) + topbar 64px + content padding 24–32px.
 * Mobile: sidebar chuyển thành drawer, topbar 56px.
 */
export function AppShell({
  children,
  userName,
  isStaff = false,
}: {
  children: React.ReactNode;
  userName?: string;
  /** Hiển thị mục điều hướng kiểm duyệt (admin / content_manager). */
  isStaff?: boolean;
}) {
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [pageTitle, setPageTitle] = React.useState<React.ReactNode>(null);

  // Đóng drawer bằng Escape
  React.useEffect(() => {
    if (!mobileOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMobileOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  return (
    <div className="flex h-dvh overflow-hidden bg-canvas">
      {/* Sidebar desktop */}
      <aside
        className={cn(
          "hidden shrink-0 transition-[width] duration-normal ease-in-out lg:block",
          collapsed ? "w-sidebar-collapsed" : "w-sidebar"
        )}
      >
        <Sidebar
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((v) => !v)}
          isStaff={isStaff}
        />
      </aside>

      {/* Drawer mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-y-0 left-0 w-sidebar max-w-[85%] shadow-lg">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label="Đóng menu"
              className="absolute right-3 top-3 z-10 inline-flex size-9 items-center justify-center rounded-md text-foreground-secondary hover:bg-subtle"
            >
              <X className="size-5" aria-hidden="true" />
            </button>
            <Sidebar collapsed={false} onToggleCollapse={() => {}} isStaff={isStaff} />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenMobileNav={() => setMobileOpen(true)} userName={userName} title={pageTitle} />
        {/* --app-sidebar: bề rộng sidebar hiện tại (0 khi <lg) để con full-bleed đúng phần main. */}
        <main
          className={cn(
            "flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 lg:p-8 [--app-sidebar:0px]",
            collapsed ? "lg:[--app-sidebar:72px]" : "lg:[--app-sidebar:256px]"
          )}
        >
          <PageTitleProvider setTitle={setPageTitle}>
            <div className="mx-auto flex min-h-full max-w-app flex-col">{children}</div>
          </PageTitleProvider>
        </main>
      </div>
    </div>
  );
}
